import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import {
  CondicionesCampana,
  LlaveService,
  MiProgramaReferidos,
  ReferidoItem,
  ResumenReferidos,
} from 'src/app/shared/services/llave.service';

/**
 * El modulo de referidos: una sola pantalla con todo.
 *
 * Cuatro bloques que aparecen segun el estado del usuario:
 *   1. Crear el usuario Aguacate, solo mientras no lo tenga. Al crearlo desaparece.
 *   2. El link para copiar y compartir.
 *   3. Cuanto lleva ganado y cuanto espera.
 *   4. Sus invitados, paginado.
 *
 * No hay forma de declarar a mano quien lo invito: el vinculo se crea solo
 * llegando por el link. Ese camino existia y se quito porque permitia que un
 * cliente viejo, con fondeos ya en la base, cobrara un bono sin que Aguacate
 * ganara nada.
 */
@Component({
  selector: 'app-referidos',
  templateUrl: './referidos.component.html',
  styleUrls: ['./referidos.component.sass'],
})
export class ReferidosComponent implements OnInit {
  cargando = true;

  llave: string | null = null;

  // --- creacion de la llave ---
  llaveBorrador = '';
  llaveEstado: 'idle' | 'verificando' | 'libre' | 'tomada' = 'idle';
  llaveMensaje = '';
  creando = false;
  private ultimaConsulta = 0;

  // --- resumen y listado ---
  resumen: ResumenReferidos | null = null;

  /**
   * Si este cliente ve referidos y con qué condiciones. Lo decide el backend:
   * programa encendido y una campaña activa de su país. `null` mientras carga.
   */
  miPrograma: MiProgramaReferidos | null = null;

  /**
   * Hay una campaña de su país: se ofrece el link y se prometen montos.
   *
   * Sin ella la pantalla ni siquiera se abre (`ReferidosGuard`), y si se
   * llegara igual, redirige al home: un venezolano no ve una campaña de
   * Chile, ni al revés. El usuario Aguacate se sigue eligiendo en el registro.
   */
  get activo(): boolean {
    return Boolean(this.miPrograma?.visible);
  }
  referidos: ReferidoItem[] = [];
  page = 1;
  limit = 10;
  total = 0;

  constructor(
    private llaveService: LlaveService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.cargar();
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  /**
   * El monto del bono como texto, o cadena vacia si no se pudo leer.
   *
   * Un solo lugar decide como se nombra: antes el numero estaba escrito a
   * mano en cinco lados y un cambio de parametro dejaba al front prometiendo
   * un monto que el backend ya no pagaba.
   */
  get bonoTexto(): string {
    return this.llaveService.bonoReferidorTexto(this.condiciones);
  }

  /** Lo mismo para el lado del invitado, que puede tener otro monto. */
  get bonoInvitadoTexto(): string {
    return this.llaveService.bonoReferidoTexto(this.condiciones);
  }

  /**
   * Las condiciones de la campaña que les tocaría a sus invitados.
   *
   * Con usuario creado salen del resumen, que mira SU campaña (asignada o la
   * global) y su cupo: si ya no le queda, vienen en `null` y no se promete.
   * Sin usuario todavía no hay resumen, y a alguien sin asignaciones le toca
   * la global, que es la que manda `programa()`.
   */
  get condiciones(): CondicionesCampana | null {
    if (this.llave) return this.resumen?.condiciones ?? null;
    return this.miPrograma?.condiciones ?? null;
  }

  /** El encabezado: cuánto gana, con qué condición, y lo que gana su invitado. */
  get fraseReferidor(): string {
    return this.llaveService.fraseReferidor(this.condiciones);
  }

  /** Lo que se le pide a quien invita, y el plazo de cada invitado. */
  get fraseCondiciones(): string {
    return [
      this.llaveService.fraseActividad(this.condiciones),
      this.llaveService.frasePlazo(this.condiciones),
    ]
      .filter(Boolean)
      .join(' ');
  }

  get link(): string {
    return this.llaveService.linkDe(this.llave);
  }

  /** Ganado o esperando, por moneda: «5 USDT · 300 Bs.». */
  totalesTexto(campo: 'liberado' | 'pendiente'): string {
    return this.llaveService.totalesTexto(this.resumen, campo);
  }

  private async cargar(): Promise<void> {
    this.cargando = true;
    try {
      // Primero si la ve: sin campaña de su país no se pide nada más, ni se
      // muestra nada. El guard ya lo frena en la ruta; esto cubre el resto.
      this.miPrograma = await this.llaveService.miPrograma();
      if (!this.miPrograma.visible) {
        this.router.navigate(['/wallet/home']);
        return;
      }

      const { llave } = await this.llaveService.miLlave();
      this.llave = llave;

      // El resumen y el listado solo tienen sentido con llave creada.
      if (llave) {
        await Promise.all([this.cargarResumen(), this.cargarReferidos(1)]);
      }
    } catch {
      this.llaveMensaje = 'No pudimos cargar tus datos. Recarga la página.';
    } finally {
      this.cargando = false;
    }
  }

  private async cargarResumen(): Promise<void> {
    try {
      this.resumen = await this.llaveService.resumen();
    } catch {
      this.resumen = null;
    }
  }

  async cargarReferidos(page: number): Promise<void> {
    if (page < 1 || (this.total && page > this.totalPaginas)) return;
    try {
      const r = await this.llaveService.misReferidos(page, this.limit);
      this.referidos = r.items;
      this.total = r.total;
      this.page = r.page;
    } catch {
      this.referidos = [];
    }
  }

  // -------------------------------------------------------------- llave

  /**
   * Normaliza en el propio campo para que lo que se ve sea lo que se guarda,
   * y consulta disponibilidad con freno entre teclas.
   *
   * El freno es un sello de tiempo y no un `debounce` de rxjs: es una sola
   * pantalla, y montar un Subject con su suscripcion para un unico campo seria
   * mas maquinaria que la que el caso pide.
   */
  async onLlaveInput(): Promise<void> {
    const normalizada = this.llaveService.normalizar(this.llaveBorrador);
    this.llaveBorrador = normalizada;

    const formato = this.llaveService.validarFormato(normalizada);
    if (!formato.valida) {
      this.llaveEstado = 'idle';
      this.llaveMensaje = normalizada ? formato.mensaje || '' : '';
      return;
    }

    this.llaveEstado = 'verificando';
    this.llaveMensaje = '';

    const sello = ++this.ultimaConsulta;
    await new Promise((r) => setTimeout(r, 400));
    if (sello !== this.ultimaConsulta) return;

    const resultado = await this.llaveService.consultarDisponibilidad(normalizada);

    // Siguio escribiendo mientras viajaba la respuesta: llega tarde y
    // corresponde a otra llave.
    if (sello !== this.ultimaConsulta) return;

    this.llaveEstado = resultado.disponible ? 'libre' : 'tomada';
    this.llaveMensaje = resultado.disponible ? '' : resultado.mensaje || '';
  }

  async crearLlave(): Promise<void> {
    if (this.llaveEstado !== 'libre' || this.creando) return;

    const confirmacion = await Swal.fire({
      title: `Tu usuario será @${this.llaveBorrador}`,
      // Definitivo, y hay que decirlo antes de crearlo: no existe camino de
      // cambio, ni por soporte. La version anterior de este texto prometia
      // "escribinos" para algo que el sistema no sabe hacer.
      text: 'Es para siempre: no se puede cambiar después. Revisa que esté bien escrito.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Crear mi usuario',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#2F4A2B',
    });
    if (!confirmacion.isConfirmed) return;

    this.creando = true;
    try {
      const { llave } = await this.llaveService.crearMiLlave(this.llaveBorrador);
      this.llave = llave;
      this.llaveBorrador = '';
      this.llaveEstado = 'idle';
      await Promise.all([this.cargarResumen(), this.cargarReferidos(1)]);

      await Swal.fire({
        title: `Listo, ya eres @${llave}`,
        // Con el programa apagado no hay link que compartir todavia, asi que
        // el mensaje no puede invitar a hacerlo.
        text: this.activo
          ? this.bonoTexto && this.llaveService.gatilloTexto(this.condiciones)
            ? `Comparte tu link y gana ${this.bonoTexto} por cada persona que ${this.llaveService.gatilloTexto(this.condiciones)}.`
            : 'Comparte tu link para empezar a invitar.'
          : 'Ya es tuyo. Cuando comiencen las promociones de referidos lo usarás desde aquí.',
        icon: 'success',
        confirmButtonColor: '#2F4A2B',
      });
    } catch (error: any) {
      // El interceptor entrega los errores en dos formas: 400, 422 y 429
      // llegan enteros, y el resto aplanados a string. Justo los que explican
      // esta pantalla caen en el segundo grupo —el 409 de "ese usuario ya está
      // en uso" o "ya tenés uno", el 404 de usuario inexistente—, así que
      // leyendo sólo el objeto se perdía el motivo y quedaba el texto genérico.
      this.llaveMensaje =
        (typeof error === 'string' ? error : error?.error?.message) ||
        'No pudimos crear tu usuario. Intenta de nuevo.';
      this.llaveEstado = 'tomada';
    } finally {
      this.creando = false;
    }
  }

  async copiarLink(): Promise<void> {
    if (await this.llaveService.copiar(this.link)) {
      Swal.fire({
        text: 'Link copiado',
        icon: 'success',
        timer: 1400,
        showConfirmButton: false,
      });
      return;
    }

    // Sin permiso de portapapeles: se le muestra el link para que lo copie
    // a mano en vez de dejarlo sin salida.
    Swal.fire({ title: 'Cópialo desde aquí', text: this.link, icon: 'info' });
  }

  async compartir(): Promise<void> {
    // Sin monto confirmado se comparte el link sin prometer una cifra.
    const texto = this.llaveService.textoInvitacion(this.link, this.condiciones);

    // `navigator.share` solo existe en móviles y contextos seguros; en
    // escritorio se cae al copiado, que es lo que el usuario iba a hacer igual.
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Aguacate Wallet', text: texto });
        return;
      } catch {
        return; // canceló el diálogo: no es un error
      }
    }
    await this.copiarLink();
  }
}
